import type { Entries } from './catalog';

/**
 * The copy of organizations, members, roles, ownership, leaving and deleting (ACC-05, ACC-06, ACC-14,
 * ACC-15, ACC-20, ACC-21, ARR-10…ARR-14).
 *
 * Each area keeps its own file so its keys travel with its screens; a key here must not repeat one of
 * `en.ts`, `es.ts` or another area.
 */
export const members: Record<'en' | 'es', Entries> = {
	en: {
		'members.sort.name': 'Name',
		'members.sort.role': 'Role',
		'members.sort.joined': 'Joined',
		'members.sort.active': 'Last active',
		'members.active.never': 'Not active yet',
		'members.show.current': 'Current members',
		'members.show.all': 'Removed members too',
		'members.you': 'You',
		'members.column.address': 'Address',
		'members.table': 'members',
		'members.column.actions': 'Actions',
		'members.requests': 'Role requests',
		'members.actions': 'Actions for {name}',
		'members.change': 'Change role',
		'members.remove': 'Remove from organization',
		'members.request': '{name} asks to be {role}',
		'members.request.note': '“{note}”',
		'members.request.grant': 'Make {role}',
		'members.request.decline': 'Decline',
		'members.request.declined': 'Request declined.',

		'role.dialog.title': 'Change the role of {name}',
		'role.dialog.lead': 'Choose what {name} can do in {organization}.',
		'role.dialog.submit': 'Change role',
		'role.dialog.products': 'In each product',
		'role.dialog.unpublished': '{product} does not describe its roles yet.',
		'role.dialog.self': 'You are lowering your own role. Only an owner or administrator can give it back.',
		'role.dialog.owner': '{name} will be able to do everything you can, including deleting the organization and removing you.',
		'role.dialog.last': 'You are the only owner. Make someone else an owner first, or transfer ownership.',
		'role.help.owner.long': 'Everything, including ownership, deleting the organization and its members.',
		'role.help.admin.long': 'Members, invitations and the organization’s settings.',
		'role.help.developer.long': 'Works in the organization’s products; cannot manage members.',
		'role.help.viewer.long': 'Reads what the organization has; changes nothing.',

		'holdings.title': 'In the products',
		'holdings.none': 'Nothing in the products.',
		'holdings.unavailable': '{product} could not be checked.',
		'holdings.away': 'What the products hold: not available right now.',
		'holdings.billing': 'Billed while it exists',
		'holdings.reading': 'Checking the products…',

		'leave.action': 'Leave organization',
		'leave.title': 'Leave {organization}?',
		'leave.body': 'You lose access to {organization} in every product.',
		'leave.lost': 'Your membership and your role in it',
		'leave.kept': 'Your Beyond account, and what you did here',
		'leave.recovery': 'An owner or administrator can invite you again.',
		'organization.left': 'You left {organization}.',

		'danger.title': 'Ownership and deletion',
		'transfer.action': 'Transfer ownership',
		'transfer.title': 'Transfer ownership of {organization}',
		'transfer.member': 'New owner',
		'transfer.lead': 'They become an owner, and you become an administrator.',
		'transfer.confirm': 'Type {organization} to confirm',
		'transfer.submit': 'Transfer ownership',
		'transfer.done': '{name} is now an owner. You are an administrator.',
		'transfer.none': 'Invite someone first: there is nobody to hand the organization to.',

		'delete.action': 'Delete organization',
		'delete.title': 'Delete {organization}?',
		'delete.lead': 'Everyone loses access now. On {date} it is deleted with what the products hold for it. Until then, an owner can cancel.',
		'delete.confirm': 'Type {organization} to confirm',
		'delete.submit': 'Delete organization',
		'delete.done': '{organization} will be deleted on {date}.',
		'closing.title': 'Scheduled for deletion',
		'closing.lead': 'Deleted on {date}. Members have no access until then.',
		'closing.cancel': 'Cancel deletion',
		'closing.cancelled': '{organization} will not be deleted.',

		'settings.slug': 'Short name',
		'settings.slug.help': 'Unique across Beyond. Lowercase letters, digits and hyphens.',
		'settings.slug.invalid': 'Use 3 to 30 lowercase letters, digits and hyphens, with no hyphen at either end or two together.',
		'settings.slug.owners': 'Only owners change the short name.',
		'settings.slug.done': 'Short name changed. {previous} stays reserved for this organization.',
		'settings.id': 'Organization ID',
		'settings.logo': 'Logo',
		'settings.logo.help': 'PNG, JPEG or WebP, up to 256 KB.',
		'settings.logo.choose': 'Choose a picture',
		'settings.logo.remove': 'Remove logo',
		'settings.logo.done': 'Logo updated.',
		'settings.saved': 'Settings saved.',
		'organizations.duplicate': 'You already have an organization named {name}. People will not be able to tell them apart.',
		'organizations.duplicate.continue': 'Create it anyway',

		'audit.title': 'Activity',
		'audit.empty': 'Nothing yet.',
		'audit.more': 'Show older',
		'audit.end': 'Nothing older.',
		'audit.someone': 'Someone',
		'audit.system': 'Beyond',
		'audit.membership.set': '{actor} made {subject} {role}',
		'audit.membership.remove': '{actor} removed {subject}',
		'audit.membership.leave': '{subject} left',
		'audit.invitation.create': '{actor} invited someone as {role}',
		'audit.invitation.revoke': '{actor} withdrew an invitation',
		'audit.invitation.accept': '{subject} joined',
		'audit.organization.create': '{actor} created the organization',
		'audit.organization.rename': '{actor} renamed the organization',
		'audit.organization.slug': '{actor} changed the short name',
		'audit.organization.handle': '{actor} changed the short name',
		'audit.organization.logo': '{actor} changed the logo',
		'audit.organization.close': '{actor} deleted the organization',
		'audit.organization.restore': '{actor} cancelled the deletion',
		'audit.ownership.transfer': '{actor} made {subject} an owner',
		'audit.request.create': '{subject} asked to be {role}',
		'audit.request.decline': '{actor} declined {subject}’s request',
		'audit.other': '{actor}: {action}',
		'audit.column.what': 'What happened',
		'audit.column.when': 'When',
		'error.slug.unavailable': 'That short name is not available. Choose another one.',
		'error.confirmation': 'What you typed does not match the name.',
		'organizations.duplicate.short': 'You already have an organization with this name.'
	},
	es: {
		'members.sort.name': 'Nombre',
		'members.sort.role': 'Rol',
		'members.sort.joined': 'Ingreso',
		'members.sort.active': 'Última actividad',
		'members.active.never': 'Sin actividad todavía',
		'members.show.current': 'Miembros actuales',
		'members.show.all': 'También los quitados',
		'members.you': 'Tú',
		'members.column.address': 'Dirección',
		'members.table': 'los miembros',
		'members.column.actions': 'Acciones',
		'members.requests': 'Solicitudes de rol',
		'members.actions': 'Acciones para {name}',
		'members.change': 'Cambiar rol',
		'members.remove': 'Quitar de la organización',
		'members.request': '{name} pide ser {role}',
		'members.request.note': '«{note}»',
		'members.request.grant': 'Hacer {role}',
		'members.request.decline': 'Rechazar',
		'members.request.declined': 'Solicitud rechazada.',

		'role.dialog.title': 'Cambiar el rol de {name}',
		'role.dialog.lead': 'Elige qué puede hacer {name} en {organization}.',
		'role.dialog.submit': 'Cambiar rol',
		'role.dialog.products': 'En cada producto',
		'role.dialog.unpublished': '{product} todavía no describe sus roles.',
		'role.dialog.self': 'Estás bajando tu propio rol. Solo un propietario o administrador puede devolvértelo.',
		'role.dialog.owner': '{name} podrá hacer todo lo que tú haces, incluido eliminar la organización y quitarte.',
		'role.dialog.last': 'Eres el único propietario. Primero haz propietario a otra persona o transfiere la propiedad.',
		'role.help.owner.long': 'Todo, incluida la propiedad, eliminar la organización y a sus miembros.',
		'role.help.admin.long': 'Miembros, invitaciones y los ajustes de la organización.',
		'role.help.developer.long': 'Trabaja en los productos de la organización; no gestiona miembros.',
		'role.help.viewer.long': 'Lee lo que tiene la organización; no cambia nada.',

		'holdings.title': 'En los productos',
		'holdings.none': 'Nada en los productos.',
		'holdings.unavailable': 'No se pudo consultar {product}.',
		'holdings.away': 'Lo que tienen los productos: no disponible en este momento.',
		'holdings.billing': 'Se cobra mientras exista',
		'holdings.reading': 'Consultando los productos…',

		'leave.action': 'Salir de la organización',
		'leave.title': '¿Salir de {organization}?',
		'leave.body': 'Pierdes el acceso a {organization} en todos los productos.',
		'leave.lost': 'Tu membresía y tu rol en ella',
		'leave.kept': 'Tu cuenta de Beyond y lo que hiciste aquí',
		'leave.recovery': 'Un propietario o administrador puede volver a invitarte.',
		'organization.left': 'Saliste de {organization}.',

		'danger.title': 'Propiedad y eliminación',
		'transfer.action': 'Transferir la propiedad',
		'transfer.title': 'Transferir la propiedad de {organization}',
		'transfer.member': 'Nuevo propietario',
		'transfer.lead': 'Pasa a ser propietario y tú pasas a ser administrador.',
		'transfer.confirm': 'Escribe {organization} para confirmar',
		'transfer.submit': 'Transferir la propiedad',
		'transfer.done': '{name} ahora es propietario. Tú eres administrador.',
		'transfer.none': 'Primero invita a alguien: no hay a quién entregar la organización.',

		'delete.action': 'Eliminar organización',
		'delete.title': '¿Eliminar {organization}?',
		'delete.lead': 'Todos pierden el acceso ahora. El {date} se elimina junto con lo que tienen los productos para ella. Hasta entonces, un propietario puede cancelarlo.',
		'delete.confirm': 'Escribe {organization} para confirmar',
		'delete.submit': 'Eliminar organización',
		'delete.done': '{organization} se eliminará el {date}.',
		'closing.title': 'Programada para eliminarse',
		'closing.lead': 'Se elimina el {date}. Hasta entonces los miembros no tienen acceso.',
		'closing.cancel': 'Cancelar la eliminación',
		'closing.cancelled': '{organization} ya no se eliminará.',

		'settings.slug': 'Nombre corto',
		'settings.slug.help': 'Único en Beyond. Letras minúsculas, números y guiones.',
		'settings.slug.invalid': 'Usa de 3 a 30 letras minúsculas, números y guiones, sin guion al principio, al final ni dos seguidos.',
		'settings.slug.owners': 'Solo los propietarios cambian el nombre corto.',
		'settings.slug.done': 'Nombre corto cambiado. {previous} queda reservado para esta organización.',
		'settings.id': 'ID de la organización',
		'settings.logo': 'Logo',
		'settings.logo.help': 'PNG, JPEG o WebP, hasta 256 KB.',
		'settings.logo.choose': 'Elegir una imagen',
		'settings.logo.remove': 'Quitar logo',
		'settings.logo.done': 'Logo actualizado.',
		'settings.saved': 'Ajustes guardados.',
		'organizations.duplicate': 'Ya tienes una organización llamada {name}. Nadie podrá distinguirlas.',
		'organizations.duplicate.continue': 'Crearla de todos modos',

		'audit.title': 'Actividad',
		'audit.empty': 'Nada todavía.',
		'audit.more': 'Mostrar anteriores',
		'audit.end': 'Nada anterior.',
		'audit.someone': 'Alguien',
		'audit.system': 'Beyond',
		'audit.membership.set': '{actor} hizo a {subject} {role}',
		'audit.membership.remove': '{actor} quitó a {subject}',
		'audit.membership.leave': '{subject} salió',
		'audit.invitation.create': '{actor} invitó a alguien como {role}',
		'audit.invitation.revoke': '{actor} retiró una invitación',
		'audit.invitation.accept': '{subject} se unió',
		'audit.organization.create': '{actor} creó la organización',
		'audit.organization.rename': '{actor} renombró la organización',
		'audit.organization.slug': '{actor} cambió el nombre corto',
		'audit.organization.handle': '{actor} cambió el nombre corto',
		'audit.organization.logo': '{actor} cambió el logo',
		'audit.organization.close': '{actor} eliminó la organización',
		'audit.organization.restore': '{actor} canceló la eliminación',
		'audit.ownership.transfer': '{actor} hizo propietario a {subject}',
		'audit.request.create': '{subject} pidió ser {role}',
		'audit.request.decline': '{actor} rechazó la solicitud de {subject}',
		'audit.other': '{actor}: {action}',
		'audit.column.what': 'Qué pasó',
		'audit.column.when': 'Cuándo',
		'error.slug.unavailable': 'Ese nombre corto no está disponible. Elige otro.',
		'error.confirmation': 'Lo que escribiste no coincide con el nombre.',
		'organizations.duplicate.short': 'Ya tienes una organización con este nombre.'
	}
};
